/**
 * How wide the longest line of a name would set, in ems of the serif, with
 * the name on at most two lines and no word broken — before the browser
 * has laid anything out.
 *
 * The profile name shrinks to fit its slot rather than wrap without end,
 * the rule iOS sets it by (`ProfileHeaderView`: `lineLimit(1)`,
 * `minimumScaleFactor`), and CSS has no "shrink until it fits". What it
 * has is container units, so the stylesheet can divide the slot's width by
 * this estimate and arrive at the size at which the two meet — provided
 * the estimate is in hand at render time, on the server too, or the first
 * paint would be the wrong size.
 *
 * Two lines rather than iOS's one: the web's slot shares its row with the
 * corner doors and is half as wide, and one line there put an ordinary
 * two-word name at the floor. The estimate is the best two-line setting —
 * the split of the words that keeps the longer line shortest — so a name
 * that fits on one line at full size still gets one line, a two-word name
 * gets a line each at full size, and only a word wider than the slot on
 * its own shrinks it, down to the floor.
 *
 * The advances are Georgia's, the widest face in the serif stack (`ui-serif`
 * is New York on Safari and about seven percent narrower; Times narrower
 * still), letter by letter for the two alphabets a name here is likely to
 * be written in and by class for the rest. Erring wide is the safe side: a
 * name guessed wider than it is shrinks a little further than it needed
 * to, while a name guessed narrower is broken or cut at a size where
 * shrinking would have fit it whole.
 */
const LETTERS: Record<string, number> = {
  a: 0.5,
  b: 0.56,
  c: 0.45,
  d: 0.57,
  e: 0.48,
  f: 0.33,
  g: 0.51,
  h: 0.58,
  i: 0.29,
  j: 0.29,
  k: 0.54,
  l: 0.29,
  m: 0.88,
  n: 0.59,
  o: 0.54,
  p: 0.57,
  q: 0.56,
  r: 0.41,
  s: 0.43,
  t: 0.35,
  u: 0.58,
  v: 0.5,
  w: 0.74,
  x: 0.5,
  y: 0.49,
  z: 0.44,
  A: 0.67,
  B: 0.65,
  C: 0.64,
  D: 0.75,
  E: 0.65,
  F: 0.6,
  G: 0.73,
  H: 0.81,
  I: 0.39,
  J: 0.52,
  K: 0.69,
  L: 0.6,
  M: 0.93,
  N: 0.77,
  O: 0.74,
  P: 0.61,
  Q: 0.74,
  R: 0.7,
  S: 0.56,
  T: 0.62,
  U: 0.76,
  V: 0.67,
  W: 0.98,
  X: 0.71,
  Y: 0.62,
  Z: 0.6,
  а: 0.5,
  б: 0.54,
  в: 0.52,
  г: 0.44,
  д: 0.56,
  е: 0.48,
  ё: 0.48,
  ж: 0.79,
  з: 0.47,
  и: 0.61,
  й: 0.61,
  к: 0.55,
  л: 0.58,
  м: 0.72,
  н: 0.61,
  о: 0.54,
  п: 0.6,
  р: 0.57,
  с: 0.45,
  т: 0.47,
  у: 0.49,
  ф: 0.75,
  х: 0.5,
  ц: 0.6,
  ч: 0.57,
  ш: 0.86,
  щ: 0.86,
  ъ: 0.58,
  ы: 0.76,
  ь: 0.5,
  э: 0.49,
  ю: 0.79,
  я: 0.55,
  А: 0.67,
  Б: 0.65,
  В: 0.65,
  Г: 0.58,
  Д: 0.72,
  Е: 0.65,
  Ё: 0.65,
  Ж: 0.98,
  З: 0.61,
  И: 0.82,
  Й: 0.82,
  К: 0.69,
  Л: 0.76,
  М: 0.93,
  Н: 0.81,
  О: 0.74,
  П: 0.81,
  Р: 0.61,
  С: 0.64,
  Т: 0.62,
  У: 0.66,
  Ф: 0.77,
  Х: 0.71,
  Ц: 0.81,
  Ч: 0.75,
  Ш: 1.1,
  Щ: 1.1,
  Ъ: 0.76,
  Ы: 0.97,
  Ь: 0.64,
  Э: 0.66,
  Ю: 1.07,
  Я: 0.7,
  ".": 0.27,
  "-": 0.37,
  "'": 0.22,
  "’": 0.23,
  _: 0.64,
  ",": 0.27,
};
const DIGIT = 0.61;
const LOWER = 0.55;
const UPPER = 0.75;
const OTHER = 0.4;
const WIDE = 1;
const PICTURE = 1.2;
const SPACE = 0.24;
const MARGIN = 1.03;

function advance(char: string): number {
  const known = LETTERS[char];
  if (known !== undefined) return known;
  const code = char.codePointAt(0) ?? 0;
  // Combining marks ride on the letter before them.
  if ((code >= 0x0300 && code <= 0x036f) || (code >= 0x1ab0 && code <= 0x1aff)) return 0;
  if (code >= 0x30 && code <= 0x39) return DIGIT;
  // CJK ideographs, kana and hangul set on a full square.
  if (
    (code >= 0x2e80 && code <= 0x9fff) ||
    (code >= 0xac00 && code <= 0xd7af) ||
    (code >= 0xf900 && code <= 0xfaff) ||
    (code >= 0xff00 && code <= 0xffef)
  ) {
    return WIDE;
  }
  // Emoji and the rest of the astral planes.
  if (code > 0xffff) return PICTURE;
  // A letter outside the two alphabets above — accented Latin, Greek,
  // Armenian — by its case; anything caseless by a middling width.
  const lower = char.toLowerCase();
  if (lower === char.toUpperCase()) return OTHER;
  return char === lower ? LOWER : UPPER;
}

function wordEms(word: string, letter: number): number {
  let ems = 0;
  for (const char of word) ems += advance(char) + letter;
  return ems;
}

function lineEms(words: number[], gap: number): number {
  return words.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, words.length - 1);
}

/**
 * Space a reader has added on top of the face's own advances, in ems:
 * `letter` after every character, `word` on every space. Both zero for the
 * page as it is designed.
 */
export type NameSpacing = { letter?: number; word?: number };

/** The estimate, never below one em so the stylesheet never divides by nothing. */
export function nameLineEms(name: string, spacing: NameSpacing = {}): number {
  const letter = spacing.letter ?? 0;
  // A space is a character too, so it takes the letter-spacing as well.
  const gap = SPACE + letter + (spacing.word ?? 0);
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => wordEms(word, letter));
  let best = lineEms(words, gap);
  // Every place the second line could start; the browser fills the first
  // line greedily, which never needs more lines than the best split does.
  for (let split = 1; split < words.length; split++) {
    best = Math.min(
      best,
      Math.max(lineEms(words.slice(0, split), gap), lineEms(words.slice(split), gap)),
    );
  }
  return Math.max(1, Math.round(best * MARGIN * 100) / 100);
}
